import * as d3 from 'd3';
import { useCallback, useEffect, useRef, useState } from 'react';
import useMobileLayout from '@/hooks/use-mobile-layout';
type Transaction = {
  date: string;
  amount: number;
  counterparty?: string;
  reference?: string;
};
type StructuringChartProps = {
  title?: string;
  transactions: Transaction[];
  threshold: number;
  thresholdLabel?: string;
  currency?: string;
};
const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];
const fmt = (v: number, currency = 'USD') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency,
  maximumFractionDigits: 0
}).format(v);
const fmtDate = (d: Date) => d.toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric'
});
const StructuringChart = ({
  transactions = [],
  threshold,
  thresholdLabel,
  currency = 'USD'
}: StructuringChartProps) => {
  const isMobile = useMobileLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    t: Transaction;
  } | null>(null);
  const [width, setWidth] = useState(0);

  // biome-ignore lint/plugin: ref-callback identity gates React attach/detach for the ResizeObserver
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      containerRef.current = node;
      setWidth(node.clientWidth);
      const ro = new ResizeObserver(entries => {
        const w = entries[0]?.contentRect.width;
        if (w && w > 0) setWidth(w);
      });
      ro.observe(node);
      return () => ro.disconnect();
    }
  }, []);
  const sorted = [...transactions].map(t => ({
    ...t,
    amount: Number(t.amount),
    _date: new Date(t.date)
  })).filter(t_0 => !Number.isNaN(t_0._date.getTime()) && !Number.isNaN(t_0.amount)).sort((a, b) => a._date.getTime() - b._date.getTime());
  const counterparties = [...new Set(sorted.map(t_1 => t_1.counterparty ?? 'Unknown'))];
  const colorMap = Object.fromEntries(counterparties.map((cp, i) => [cp, COLORS[i % COLORS.length]]));
  useEffect(() => {
    if (!svgRef.current || !sorted.length || !width) return;
    const el = svgRef.current;
    const svg = d3.select(el);
    svg.selectAll('*').remove();
    const W = width;
    const H = 320;
    const margin = {
      top: 24,
      right: 20,
      bottom: 40,
      left: 56
    };
    const iw = W - margin.left - margin.right;
    const ih = H - margin.top - margin.bottom;
    svg.attr('width', W).attr('height', H);
    const dates = sorted.map(t_2 => t_2._date);
    const amounts = sorted.map(t_3 => t_3.amount);
    const xExtent = d3.extent(dates) as [Date, Date];
    const xPad = (xExtent[1].getTime() - xExtent[0].getTime()) * 0.05 || 86400000;
    const x = d3.scaleTime().domain([new Date(xExtent[0].getTime() - xPad), new Date(xExtent[1].getTime() + xPad)]).range([0, iw]);
    const yMin = Math.min(d3.min(amounts)!, threshold) * 0.995;
    const yMax = Math.max(d3.max(amounts)!, threshold) * 1.005;
    const yPad = (yMax - yMin) * 0.15;
    const y = d3.scaleLinear().domain([yMin - yPad, yMax + yPad]).range([ih, 0]).nice();
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Gridlines
    const yTicks = y.ticks(5);
    g.append('g').selectAll('line').data(yTicks).join('line').attr('x1', 0).attr('y1', d => y(d)).attr('x2', iw).attr('y2', d_0 => y(d_0)).attr('stroke', 'var(--border-color-tertiary)').attr('stroke-width', 1);

    // Y-axis labels
    const fmtAxis = (v: number) => {
      if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
      if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
      return `$${v.toFixed(0)}`;
    };
    g.append('g').selectAll('text').data(yTicks).join('text').attr('x', -12).attr('y', d_1 => y(d_1)).attr('text-anchor', 'end').attr('dominant-baseline', 'middle').attr('font-size', 11).attr('font-family', 'inherit').attr('fill', 'var(--text-color-tertiary)').text(d_2 => fmtAxis(d_2));

    // X-axis labels — limit to ~6 ticks max regardless of data density
    const maxXTicks = Math.min(6, Math.floor(iw / 80));
    const xTicks = x.ticks(maxXTicks);
    g.append('g').selectAll('text').data(xTicks).join('text').attr('x', d_3 => x(d_3)).attr('y', ih + 24).attr('text-anchor', 'middle').attr('font-size', 11).attr('font-family', 'inherit').attr('fill', 'var(--text-color-tertiary)').text(d_4 => fmtDate(d_4));

    // Threshold line
    g.append('line').attr('x1', -4).attr('y1', y(threshold)).attr('x2', iw + 4).attr('y2', y(threshold)).attr('stroke', 'var(--text-color-error)').attr('stroke-width', 1.5).attr('stroke-dasharray', '6,4');
    g.append('text').attr('x', iw).attr('y', y(threshold) - 8).attr('text-anchor', 'end').attr('font-size', 11).attr('font-weight', 600).attr('font-family', 'inherit').attr('fill', 'var(--text-color-error)').text(thresholdLabel ?? `${fmtAxis(threshold)} Reporting Threshold`);

    // Lines per counterparty
    const line = d3.line<(typeof sorted)[0]>().x(d_5 => x(d_5._date)).y(d_6 => y(d_6.amount)).curve(d3.curveMonotoneX);
    for (const cp_0 of counterparties) {
      const cpData = sorted.filter(t_4 => (t_4.counterparty ?? 'Unknown') === cp_0);
      if (cpData.length < 2) continue;
      g.append('path').datum(cpData).attr('fill', 'none').attr('stroke', colorMap[cp_0]).attr('stroke-width', 1.5).attr('stroke-opacity', 0.4).attr('d', line);
    }

    // Dots
    g.selectAll<SVGCircleElement, (typeof sorted)[0]>('circle.dot').data(sorted).join('circle').attr('class', 'dot').attr('cx', d_7 => x(d_7._date)).attr('cy', d_8 => y(d_8.amount)).attr('r', 5).attr('fill', d_9 => colorMap[d_9.counterparty ?? 'Unknown']).attr('stroke', 'var(--background-color-primary)').attr('stroke-width', 2).attr('cursor', 'pointer').on('mouseenter', function (event, d_10) {
      d3.select(this).transition().duration(100).attr('r', 7);
      const rect = el.getBoundingClientRect();
      setTooltip({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        t: d_10
      });
    }).on('mouseleave', function () {
      d3.select(this).transition().duration(100).attr('r', 5);
      setTooltip(null);
    });
    return () => {
      svg.selectAll('*').remove();
    };
  }, [sorted, threshold, thresholdLabel, currency, counterparties, colorMap, width]);
  return <div className="w-full">
      <div ref={measuredRef} className="relative">
        <svg ref={svgRef} style={{
        display: 'block',
        width: '100%'
      }} />

        {tooltip && <div className="pointer-events-none absolute z-10 rounded-md px-2.5 py-1.5 shadow-md max-w-[300px]"
      // A phone-width chart has no room right of a point on its right half, so the tooltip flips left.
      style={{
        ...(isMobile && tooltip.x > width / 2 ? {
          right: width - tooltip.x + 12
        } : {
          left: tooltip.x + 12
        }),
        top: tooltip.y - 52,
        backgroundColor: 'var(--text-color-primary)'
      }}>
            <p className="text-[11px] font-semibold" style={{
          color: 'var(--background-color-primary)'
        }}>
              {fmt(tooltip.t.amount, currency)}
            </p>
            <p className="text-[10px] break-words" style={{
          color: '#a0a0a0'
        }}>
              {fmtDate(new Date(tooltip.t.date))}
              {tooltip.t.counterparty ? ` · ${tooltip.t.counterparty}` : ''}
            </p>
          </div>}
      </div>

      {counterparties.length > 1 && <div className="mt-3 flex flex-wrap justify-center gap-x-8 gap-y-2">
          {counterparties.map(cp_1 => <div key={cp_1} className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 flex-none rounded-full" style={{
          backgroundColor: colorMap[cp_1]
        }} />
              <span className="text-caption-1 text-tertiary fp-text">{cp_1}</span>
            </div>)}
        </div>}
    </div>;
};
export default StructuringChart;